/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import type { EbtHookReturnType } from '@modules/ebt/hook'
import useEbt from '@modules/ebt/hook'

type EventType = 'trial_extension_requested' | 'trial_extension_succeeded'

type TrialExtensionRequestedPayloadSchema = {
  eventType: 'trial_extension_requested'
  payload: {
    source_component_id: string
    allow_trial_extension?: boolean
  }
}

type TrialExtensionSucceededPayloadSchema = {
  eventType: 'trial_extension_succeeded'
  payload: {
    trial_extended_with: number
    source_component_id: string
    allow_trial_extension?: boolean
  }
}

type EventPayloadSchema =
  | TrialExtensionRequestedPayloadSchema
  | TrialExtensionSucceededPayloadSchema

export const useEbtTrialExtension = (): EbtHookReturnType<EventType, EventPayloadSchema> => {
  const reportEbtEvent = useEbt<EventPayloadSchema>(
    {
      eventType: 'trial_extension_requested',
      schemaProps: {
        source_component_id: {
          description: 'Identifier of the component where user clicked extend trial',
          keywordType: 'keyword',
        },
        allow_trial_extension: {
          description: 'Whether the trial extension is allowed',
          keywordType: 'boolean',
        },
      },
    },
    {
      eventType: 'trial_extension_succeeded',
      schemaProps: {
        trial_extended_with: {
          description: 'Number of days the trial was extended with',
          keywordType: 'integer',
        },
        source_component_id: {
          description: 'Identifier of the component where user clicked extend trial',
          keywordType: 'keyword',
        },
        allow_trial_extension: {
          description: 'Whether the trial extension is allowed',
          keywordType: 'boolean',
        },
      },
    },
  )

  return {
    reportTrialExtensionRequestedEbt: ({ source_component_id, allow_trial_extension }) =>
      reportEbtEvent('trial_extension_requested', {
        source_component_id,
        allow_trial_extension,
      }),
    reportTrialExtensionSucceededEbt: ({
      trial_extended_with,
      source_component_id,
      allow_trial_extension,
    }) =>
      reportEbtEvent('trial_extension_succeeded', {
        trial_extended_with,
        source_component_id,
        allow_trial_extension,
      }),
  }
}
